<script setup>
import { ref, computed } from "vue";

/**
 * «Что решают суды по вашему делу» — практика подуслуги с фильтром по обстоятельствам.
 *
 * Обстоятельства — те же, что юристы ведут в /admin/court/factors: отметил похожее на
 * свою ситуацию — видишь, сколько таких дел и чем они кончились (обстоятельство «Исход»).
 * Внутри одного обстоятельства варианты складываются через «или», разные обстоятельства —
 * через «и». Считает браузер по матрице rows (см. CourtPracticeBlock), поэтому счётчики
 * у вариантов честные по всем опубликованным делам, а не по первой странице.
 *
 * SSR: без выбора блок рендерит свежие решения со ссылками на разборы — это видят
 * поисковики. Карточки, которых нет в первой выдаче, догружаются по id.
 */
const props = defineProps({ data: { type: Object, required: true } });

const d = computed(() => props.data);
const region = ref(props.data.region || "all");
const sel = ref({}); // factorId → [optionId]
const showAllFilters = ref(false);
// На телефоне фильтр свёрнут: иначе до цифр и решений — экран чипов. На широком всегда открыт.
const filtersOpen = ref(false);
const limit = ref(5);
const cards = ref({ ...(props.data.cards || {}) });
const loading = ref(false);

const FILTERS_OPEN = 4;
const filters = computed(() => d.value.filters || []);
const shownFilters = computed(() => (showAllFilters.value ? filters.value : filters.value.slice(0, FILTERS_OPEN)));

const optLabel = computed(() => {
  const m = {};
  for (const f of filters.value) for (const o of f.options) m[o.id] = o.label;
  return m;
});

const baseRows = computed(() =>
  region.value === "all" ? d.value.rows : d.value.rows.filter((r) => (r.r || "") === region.value)
);

// Реальные id вариантов обстоятельства (без синтетического «Не установлено»).
function realOptIds(fid) {
  const f = filters.value.find((x) => Number(x.id) === Number(fid));
  return f ? f.options.filter((o) => !o.none).map((o) => o.id) : [];
}

function matches(row, except = null) {
  for (const [fid, opts] of Object.entries(sel.value)) {
    if (Number(fid) === except || !opts.length) continue;
    // «Не установлено» — отрицательный id: дело без любого варианта обстоятельства.
    const hitReal = opts.filter((o) => o > 0).some((o) => row.f.includes(o));
    const hitNone =
      opts.some((o) => o < 0) && !realOptIds(fid).some((o) => row.f.includes(o));
    if (!(hitReal || hitNone)) return false;
  }
  return true;
}

const filtered = computed(() => baseRows.value.filter((r) => matches(r)));
const selectedCount = computed(() => Object.values(sel.value).reduce((n, a) => n + a.length, 0));

function optionCount(fid, oid) {
  let n = 0;
  const none = oid < 0;
  const real = none ? realOptIds(fid) : null;
  for (const r of baseRows.value) {
    const has = none ? !real.some((o) => r.f.includes(o)) : r.f.includes(oid);
    if (has && matches(r, fid)) n++;
  }
  return n;
}
const isOn = (fid, oid) => (sel.value[fid] || []).includes(oid);

function toggle(fid, oid) {
  const cur = sel.value[fid] || [];
  const next = cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid];
  sel.value = { ...sel.value, [fid]: next };
  limit.value = 5;
  ensureCards();
}
function reset() {
  sel.value = {};
  limit.value = 5;
}
function pickRegion(code) {
  region.value = code;
  limit.value = 5;
  ensureCards();
}

// ── Исходы ──────────────────────────────────────────────────────────────────
// Цвет по порядку варианта, «Другое» всегда серое. Красный не берём: он у звонка.
const BAR = ["bg-indigo-600", "bg-amber-400", "bg-teal-500", "bg-violet-400", "bg-slate-400"];
const CHIP = [
  "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  "bg-amber-50 text-amber-800 ring-amber-600/20",
  "bg-teal-50 text-teal-700 ring-teal-600/20",
  "bg-violet-50 text-violet-700 ring-violet-600/20",
  "bg-slate-100 text-slate-700 ring-slate-500/20",
];
const outcomeOpts = computed(() => {
  const opts = d.value.outcome?.options || [];
  let i = 0;
  return opts.map((o) => {
    const other = o.label === "Другое";
    const k = other ? -1 : i++;
    return {
      ...o,
      bar: other ? "bg-gray-300" : BAR[k % BAR.length],
      chip: other ? "bg-gray-100 text-gray-600 ring-gray-500/20" : CHIP[k % CHIP.length],
    };
  });
});
const outcomeById = computed(() => Object.fromEntries(outcomeOpts.value.map((o) => [o.id, o])));

const stats = computed(() => {
  const withOutcome = filtered.value.filter((r) => r.o);
  const n = withOutcome.length;
  const rows = outcomeOpts.value
    .map((o) => {
      const c = withOutcome.filter((r) => r.o === o.id).length;
      return { ...o, count: c, pct: n ? Math.round((c / n) * 100) : 0 };
    })
    .filter((r) => r.count > 0);
  return { n, rows };
});

// ── Решения ─────────────────────────────────────────────────────────────────
const visibleRows = computed(() => filtered.value.slice(0, limit.value).filter((r) => cards.value[r.id]));
const moreLeft = computed(() => Math.max(0, filtered.value.length - limit.value));

async function ensureCards() {
  const need = filtered.value.slice(0, limit.value).map((r) => r.id).filter((id) => !cards.value[id]);
  if (!need.length || typeof window === "undefined") return;
  loading.value = true;
  try {
    const res = await fetch("/api/court/cards?ids=" + need.slice(0, 30).join(","), { headers: { Accept: "application/json" } });
    if (res.ok) cards.value = { ...cards.value, ...(await res.json()) };
  } catch (e) {
    /* сеть: карточки просто не появятся, фильтр и цифры работают */
  } finally {
    loading.value = false;
  }
}
function showMore() {
  limit.value += 10;
  ensureCards();
}

/** Отмеченные обстоятельства, которые есть в этом деле: «почему оно в выдаче». */
function matchedLabels(row) {
  const on = Object.values(sel.value).flat();
  return row.f.filter((o) => on.includes(o)).map((o) => optLabel.value[o]).filter(Boolean);
}

function plural(n, one, few, many) {
  const t = n % 100, o = n % 10;
  if (t >= 11 && t <= 14) return many;
  if (o === 1) return one;
  if (o >= 2 && o <= 4) return few;
  return many;
}
</script>

<template>
  <section class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
    <!-- Шапка -->
    <div class="px-5 pt-5 pb-4 border-b border-gray-100">
      <h2 class="text-lg md:text-xl font-bold text-gray-900 leading-snug">{{ d.title }}</h2>
      <p class="mt-1 text-sm text-gray-600">
        {{ d.total }} {{ plural(d.total, "решение", "решения", "решений") }} судов по теме с нашим разбором.
        <template v-if="filters.length">Отметьте обстоятельства, похожие на ваши, — покажем, чем кончались такие дела.</template>
      </p>

      <!-- Регион: свой первым -->
      <div v-if="d.regions.length > 1" class="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Регион">
        <button v-for="r in d.regions" :key="r.code" type="button" @click="pickRegion(r.code)" :aria-pressed="region === r.code"
          class="px-3 py-1.5 rounded-full text-sm border transition-colors"
          :class="region === r.code ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:text-indigo-700'">
          {{ r.label }} <span :class="region === r.code ? 'text-indigo-100' : 'text-gray-400'">{{ r.count }}</span>
        </button>
        <button type="button" @click="pickRegion('all')" :aria-pressed="region === 'all'"
          class="px-3 py-1.5 rounded-full text-sm border transition-colors"
          :class="region === 'all' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:text-indigo-700'">
          Все регионы <span :class="region === 'all' ? 'text-indigo-100' : 'text-gray-400'">{{ d.total }}</span>
        </button>
      </div>
    </div>

    <div class="grid lg:grid-cols-5">
      <!-- Фильтр по обстоятельствам -->
      <div v-if="filters.length" class="lg:col-span-2 px-5 py-4 border-b lg:border-b-0 lg:border-r border-gray-100 bg-gray-50/60">
        <!-- Телефон: одна кнопка вместо простыни чипов -->
        <button type="button" @click="filtersOpen = !filtersOpen" :aria-expanded="filtersOpen"
          class="lg:hidden w-full flex items-center justify-between gap-2 rounded-xl border border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-700">
          <span>{{ selectedCount ? "Отмечено обстоятельств: " + selectedCount : "Отметить обстоятельства своего дела" }}</span>
          <svg class="w-4 h-4 transition-transform" :class="filtersOpen ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div :class="filtersOpen ? 'mt-4 lg:mt-0' : 'hidden lg:block'">
        <div class="flex items-baseline justify-between gap-2">
          <p class="text-sm font-semibold text-gray-900">Обстоятельства вашего дела</p>
          <button v-if="selectedCount" type="button" @click="reset" class="text-xs font-medium text-indigo-700 hover:underline">
            Сбросить
          </button>
        </div>

        <div class="mt-3 space-y-4">
          <fieldset v-for="f in shownFilters" :key="f.id">
            <legend class="text-sm text-gray-800">{{ f.label }}</legend>
            <p v-if="f.hint" class="text-xs text-gray-500">{{ f.hint }}</p>
            <div class="mt-1.5 flex flex-wrap gap-1.5">
              <button v-for="o in f.options" :key="o.id" type="button" @click="toggle(f.id, o.id)"
                :aria-pressed="isOn(f.id, o.id)"
                :disabled="!isOn(f.id, o.id) && !optionCount(f.id, o.id)"
                class="px-2.5 py-1 rounded-full text-sm leading-tight border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :class="isOn(f.id, o.id)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:text-indigo-700'">
                {{ o.label }}
                <span class="tabular-nums" :class="isOn(f.id, o.id) ? 'text-indigo-100' : 'text-gray-400'">{{ optionCount(f.id, o.id) }}</span>
              </button>
            </div>
          </fieldset>
        </div>

        <button v-if="filters.length > FILTERS_OPEN" type="button" @click="showAllFilters = !showAllFilters"
          :aria-expanded="showAllFilters"
          class="mt-4 inline-flex items-center gap-1 text-sm font-medium text-indigo-700 hover:text-indigo-900">
          {{ showAllFilters ? "Меньше обстоятельств" : "Ещё обстоятельства · " + (filters.length - FILTERS_OPEN) }}
          <svg class="w-3.5 h-3.5 transition-transform" :class="showAllFilters ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        </div>
      </div>

      <!-- Итог и решения -->
      <div :class="filters.length ? 'lg:col-span-3' : 'lg:col-span-5'" class="px-5 py-4" aria-live="polite">
        <!-- Ничего не подошло -->
        <div v-if="!filtered.length" class="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center">
          <p class="text-sm text-gray-700">Дел с таким сочетанием обстоятельств в базе нет.</p>
          <button type="button" @click="reset" class="mt-2 text-sm font-medium text-indigo-700 hover:underline">Снять все условия</button>
        </div>

        <template v-else>
          <!-- Чем закончились -->
          <div v-if="stats.n">
            <div class="flex items-baseline justify-between gap-2">
              <p class="text-sm font-semibold text-gray-900">
                {{ selectedCount ? "Чем закончились похожие дела" : "Чем заканчиваются такие дела" }}
              </p>
              <span class="text-xs text-gray-500 tabular-nums">по {{ stats.n }} {{ plural(stats.n, "делу", "делам", "делам") }}</span>
            </div>
            <div class="mt-2 flex h-3 rounded-full overflow-hidden bg-gray-100" role="img"
              :aria-label="stats.rows.map((r) => r.label + ' ' + r.pct + '%').join(', ')">
              <div v-for="r in stats.rows" :key="r.id" :class="r.bar" class="h-full border-r-2 border-white last:border-r-0"
                :style="{ width: r.pct + '%' }"></div>
            </div>
            <ul class="mt-2 max-w-md space-y-1">
              <li v-for="r in stats.rows" :key="r.id" class="flex items-center gap-2 text-sm text-gray-700">
                <span class="w-2.5 h-2.5 rounded-full shrink-0" :class="r.bar"></span>
                <span class="min-w-0 flex-1">{{ r.label }}</span>
                <span class="font-semibold text-gray-900 tabular-nums">{{ r.pct }}%</span>
                <span class="text-xs text-gray-400 tabular-nums">{{ r.count }}</span>
              </li>
            </ul>
            <p v-if="stats.n < 5" class="mt-2 text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-1.5">
              Похожих дел мало — это отдельные примеры, а не статистика.
            </p>
          </div>

          <!-- Решения -->
          <div :class="stats.n ? 'mt-5' : ''">
            <p class="text-sm font-semibold text-gray-900 mb-2">
              {{ selectedCount ? "Похожие решения" : "Свежие решения" }}
              <span class="font-normal text-gray-500">· {{ filtered.length }}</span>
            </p>
            <ul class="space-y-2">
              <li v-for="row in visibleRows" :key="row.id" class="rounded-xl border border-gray-200 px-3.5 py-3 hover:border-indigo-200 transition-colors">
                <div class="flex items-center justify-between gap-2 flex-wrap">
                  <span v-if="outcomeById[row.o]" class="px-2 py-0.5 rounded-md text-xs font-medium ring-1" :class="outcomeById[row.o].chip">
                    {{ outcomeById[row.o].label }}
                  </span>
                  <span class="text-xs text-gray-500">
                    {{ cards[row.id].court }}<template v-if="cards[row.id].number"> · № {{ cards[row.id].number }}</template><template v-if="cards[row.id].date"> · {{ cards[row.id].date }}</template>
                  </span>
                </div>
                <p v-if="cards[row.id].summary" class="mt-1.5 text-sm text-gray-700 line-clamp-3">{{ cards[row.id].summary }}</p>
                <div v-if="matchedLabels(row).length" class="mt-1.5 flex flex-wrap gap-1">
                  <span v-for="l in matchedLabels(row)" :key="l" class="text-[11px] rounded bg-indigo-50 text-indigo-700 px-1.5 py-0.5">✓ {{ l }}</span>
                </div>
                <div class="mt-1.5 flex items-center gap-3">
                  <a v-if="cards[row.id].url" :href="cards[row.id].url" class="text-sm font-medium text-indigo-700 hover:underline">Читать разбор →</a>
                  <a v-if="cards[row.id].src" :href="cards[row.id].src" target="_blank" rel="nofollow noopener" class="text-xs text-gray-500 hover:text-indigo-700">источник ↗</a>
                </div>
              </li>
            </ul>
            <p v-if="loading" class="mt-2 text-xs text-gray-500">Загружаю решения…</p>
            <button v-if="moreLeft" type="button" @click="showMore"
              class="mt-3 w-full py-2 rounded-xl border border-gray-200 text-sm font-medium text-indigo-700 hover:bg-indigo-50 transition-colors">
              Показать ещё {{ Math.min(10, moreLeft) }} из {{ moreLeft }}
            </button>
          </div>
        </template>

        <!-- Дальше — к юристу. Не красная: красный на странице только у звонка. -->
        <div class="mt-5 rounded-xl bg-indigo-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <p class="flex-1 text-sm text-gray-700">Практика — ориентир, а не прогноз. Шансы по вашим документам оценит юрист.</p>
          <a href="#consultation" class="shrink-0 text-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors">
            Обсудить с юристом
          </a>
        </div>
      </div>
    </div>

    <!-- Ссылка в раздел практики: страница, на которую не ведёт ни одна ссылка, обходится роботом плохо -->
    <div class="px-5 py-3 border-t border-gray-100 bg-gray-50 flex flex-wrap gap-x-5 gap-y-1">
      <a :href="d.themeUrl" class="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:underline">
        Вся практика по теме
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </a>
      <a href="/sudebnaya-praktika" class="text-sm text-gray-600 hover:text-indigo-700 hover:underline">База судебной практики</a>
    </div>
  </section>
</template>
